/* eslint-disable react/jsx-props-no-spreading */
import { useMemo } from "react";

import { Box, Tooltip } from "@mui/material";
import {
  Button,
  DotsVerticalIcon,
  Download01Icon,
  PlusIcon,
  Save01Icon,
  UploadCloud01Icon,
} from "components";
import { Permissions } from "configs";
import { gray } from "configs/colors";
import { useIsMobile, usePermission } from "hooks";
import { i18n } from "translations";

interface CommonButtonProps extends React.ComponentProps<typeof Button> {
  label?: string;
  labelI18n?: string;
  name?:
    | "add-outlined"
    | "add-contained"
    | "cancel"
    | "import"
    | "save"
    | "text"
    | "export"
    | "menu-icon";
  permission?: keyof typeof Permissions;
  labelNode?: React.ReactNode;
  tooltip?: string;
}

type TButton = {
  color?: React.ComponentProps<typeof Button>["color"];
  variant?: React.ComponentProps<typeof Button>["variant"];
  size?: React.ComponentProps<typeof Button>["size"];
  startIcon?: React.ComponentProps<typeof Button>["startIcon"];
  type?: React.ComponentProps<typeof Button>["type"];
  typographyProps?: React.ComponentProps<typeof Button>["typographyProps"];
  sx?: React.ComponentProps<typeof Button>["sx"];
};

const CommonButton: React.FC<CommonButtonProps> = ({
  label,
  name,
  labelI18n,
  permission,
  labelNode,
  tooltip,
  ...innerProps
}) => {
  const { canAccess } = usePermission();
  const isMobile = useIsMobile();

  const buttonConfig = useMemo((): TButton => {
    switch (name) {
      case "save": {
        return {
          variant: "contained",
          color: "primary",
          startIcon: <Save01Icon fontSize="small" />,
          type: "submit",
          size: "sm",
        };
      }

      case "add-contained": {
        return {
          variant: "contained",
          color: "primary",
          startIcon: <PlusIcon fontSize="small" />,
          size: "sm",
        };
      }

      case "add-outlined": {
        return {
          variant: "text",
          color: "primary",
          startIcon: <PlusIcon fontSize="small" />,
          size: "sm",
        };
      }

      case "cancel": {
        return {
          variant: "text",
          size: "icon-sm",
          color: "inherit",
          typographyProps: { color: gray[600] },
        };
      }

      case "import": {
        return {
          variant: "contained",
          color: "primary",
          size: "sm",
          startIcon: <UploadCloud01Icon fontSize="small" />,
        };
      }

      case "text": {
        return {
          variant: "text",
          color: "primary",
          size: "sm",
        };
      }

      case "export": {
        return {
          variant: "text",
          size: "sm",
          startIcon: <Download01Icon fontSize="small" />,
        };
      }

      case "menu-icon": {
        return {
          variant: "text",
          size: "sm",
          startIcon: <DotsVerticalIcon fontSize="small" />,
          sx: {
            "& .MuiButton-startIcon": { margin: 0 },
            "& .MuiButtonBase-root": { minWidth: "unset !important" },
          },
        };
      }

      default:
        return {
          variant: "contained",
          color: "primary",
          size: "sm",
        } as TButton;
    }
  }, [name]);

  const labelBtn = useMemo(() => {
    if (labelI18n) return i18n.t(labelI18n);
    if (label) return label;
    switch (name) {
      case "add-contained":
        return i18n.t("addNew");
      case "add-outlined":
        return i18n.t("addNew");
      case "save":
        return i18n.t("save");
      case "cancel":
        return i18n.t("cancel");
      case "import":
        return i18n.t("importData");
      case "export":
        return i18n.t("export");
      default:
        return label;
    }
  }, [name, label, labelI18n]);

  if (permission && !canAccess(permission)) {
    return <div />;
  }

  if (isMobile && (innerProps?.startIcon || buttonConfig.startIcon)) {
    return (
      <Tooltip title={labelBtn}>
        <Box
          sx={{
            "& .MuiButton-startIcon": { margin: 0 },
            "& .MuiButtonBase-root": { minWidth: "unset !important" },
          }}
        >
          <Button
            {...buttonConfig}
            useTypography
            onClick={innerProps?.onClick}
            disabled={innerProps?.disabled}
            startIcon={innerProps?.startIcon || buttonConfig.startIcon}
          />
        </Box>
      </Tooltip>
    );
  }

  const buttonElement = (
    <Button
      {...buttonConfig}
      useTypography
      {...innerProps}
      style={{ whiteSpace: "nowrap" }}
    >
      {labelNode || labelBtn}
    </Button>
  );

  if (tooltip) {
    return (
      <Tooltip title={tooltip}>
        <span>{buttonElement}</span>
      </Tooltip>
    );
  }

  return buttonElement;
};

export default CommonButton;
